'use client';

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useDropzone } from 'react-dropzone';
import { Upload, File, X, CheckCircle2, AlertCircle, Loader2, Pencil, Search, Sparkles, Wand2, Scissors, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { DesignPreview } from './design-preview';
import { detectInAppBrowser, inAppBrowserNotice, type InAppBrowser } from '@/lib/browser/in-app-browser';
import { PrintFileCheckCard } from './print-file-check-card';
import { evaluatePrintFile, fileCheckIssueCodes, isEditorDesign, targetFromSizeValue, type FileCheckSizeOption } from '@/lib/file-check/print-file-check';
import { fileCheckCopy, formatSizeCm } from '@/lib/file-check/print-file-check-copy';
import { rememberFileCheck, trackFileCheckFix, trackFileCheckResult } from '@/lib/file-check/file-check-memory';
import { dpiCheckerHref } from '@/lib/tools/dpi-checker-link';
import { upscaleFactorFor } from '@/lib/editor/print-resolution';
import { PDF_PREVIEW_MAX_BYTES } from '@/lib/utils/pdf-preview-policy';
import { hasEditorSize, productTemplateGuidance } from '@/lib/product-file-guidance';
import { canvasProductAllowsEditor } from '@/lib/editor/editor-size-value';
import type { ProductionSheetProfile } from '@/lib/editor/production-sheet-profile';
import type { ProductDesignEditorProps } from '@/components/editor/product-design-editor';
import { isBoogProduct, getBoogMaterial } from '@/lib/studio/products';
import { boogFileMatchesMaterial } from '@/lib/studio/boog-cart';
import { editorText } from '@/lib/studio/editor-copy';
import { ImageSearch } from './image-search';
import { parseDimensions, parseMillimeters } from '@/lib/utils/dimensions';
import {
  dataUrlToFile,
  isAcceptedArtworkFile,
  prepareArtworkFile,
  publishUploadProgress,
  uploadFileToStorage,
  UploadValidationError,
  type RegisteredUpload,
  type UploadPhase,
  type UploadProgressDetail,
} from '@/lib/uploads/client-upload';
import { POSTSCRIPT_MIME } from '@/lib/uploads/artwork-formats';
import { artworkPlaceholderDataUrl, uploadAndConvertPostScript } from '@/lib/artwork/client-artwork';
import { artworkCopy } from '@/lib/artwork/artwork-copy';
import { getOrCreateEditorSession, getCurrentEditorSessionId, recordEditorSessionEvent } from '@/lib/editor-session/client';
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_LABEL } from '@/lib/uploads/limits';
import { reportOrderPathFailure, reportOrderPathWarning } from '@/lib/monitoring/report-client-error';
import { QrCode } from '@/components/ui/qr-code';
import { HANDOFF_TTL_MS, createSessionId } from '@/lib/handoff/session';
import { useIsHandheldDevice } from '@/hooks/use-is-handheld-device';
import { compactDesignDataForPersistence, isStoredDesignDataReference } from '@/lib/designs/compact-persisted-design-data';

// Mount and download the Fabric editor only when the customer opens it.
const DesignEditorModal = dynamic<ProductDesignEditorProps>(
  () => import('@/components/editor/product-design-editor').then((module) => module.ProductDesignEditor),
  { ssr: false },
);

interface FileUploaderProps {
  draftScope?: string;
  onFileUploaded: (file: UploadedFile) => void;
  /** Na "Klaar" in de editor, als het ontwerp is opgeslagen en de editor dicht is. */
  onDesignSaved?: () => void;
  productId?: string;
  providerProductId?: string;
  productSlug?: string;    // Product slug (e.g., "boxes-fefco-0201-digital")
  providerType?: string;
  sizeValue?: string;     // Selected size (e.g., "30_x_20_cm")
  category?: string;       // Product category (e.g., "canvas")
  productName?: string;    // Product name
  uploadMode?: 'standard' | 'page-pdf';
  slotLabel?: string;
  canvasEdgeType?: string; // Canvas edge type (e.g., "white", "black", "stretched", "mirrored", "wrapped")
  isCanvasProduct?: boolean; // Is this a canvas product
  /** Editor off for this product/choice (specifications.editorAvailability); uploading stays possible. */
  editorUnavailable?: boolean;
  aiImageToolsEnabled?: boolean; // AI-fotoknoppen (Replicate) tonen of verbergen
  onCanvasEdgeTypeChange?: (edgeType: string) => void; // Callback when edge type changes
  initialFileUrl?: string; // Pre-loaded file URL
  initialFileName?: string; // Pre-loaded file name
  initialFileSize?: number;
  initialDesignData?: any; // Pre-loaded canvas design data
  locale?: string;
  finishingOptions?: Record<string, string | number>;
  guideTemplateUrl?: string | null;
  designPlacements?: Array<{ key: string; label: string; guideTemplateUrl?: string | null }>;
  designTemplateCount?: number;
  initialDesignTemplateSlug?: string;
  editorSurface?: 'cover-spread' | 'inside-pages' | 'single';
  pageCount?: number;
  productionSheetProfile?: ProductionSheetProfile;
  /** Winkelwagenopties; alleen meegegeven bij één ontwerp per product (opslaan als wederverkoopproduct). */
  getResellerOptions?: () => Record<string, string | number>;
  /** Formaatkeuzes, voor de bestandscheck-knop "kies een kleiner formaat". */
  sizeOptions?: FileCheckSizeOption[];
  /** Zet het formaat in de configurator; zonder deze prop geen formaatknop. */
  onSelectSize?: (value: string) => void;
}

interface UploadedFile {
  id: string;
  fileName: string;
  fileSize: number;
  url: string;
  providerFileId?: string;
  designData?: any; // Canvas design data from editor
  /**
   * Vectorbestand (EPS/PS/oude AI/SVG) dat nog naar PDF moet: het origineel
   * staat in `url`, `previewUrl` is alleen om te tonen en te plaatsen.
   */
  artworkConversion?: { required: true; previewUrl: string | null };
}

export function FileUploader({
  draftScope,
  onFileUploaded,
  onDesignSaved,
  productId,
  providerProductId,
  productSlug,
  providerType,
  sizeValue: configuredSizeValue,
  category,
  productName,
  uploadMode = 'standard',
  slotLabel,
  canvasEdgeType = 'wrapped',
  isCanvasProduct = false,
  editorUnavailable = false,
  aiImageToolsEnabled = true,
  onCanvasEdgeTypeChange,
  initialFileUrl,
  initialFileName,
  initialFileSize,
  initialDesignData,
  locale = 'nl',
  finishingOptions = {},
  guideTemplateUrl = null,
  designPlacements = [],
  designTemplateCount = 0,
  initialDesignTemplateSlug,
  editorSurface = 'single',
  pageCount = 1,
  productionSheetProfile,
  getResellerOptions,
  sizeOptions,
  onSelectSize,
}: FileUploaderProps) {
  const isBoog = isBoogProduct(productSlug);
  // Canvas goes through the canvas-edge flow without the editor, except for the
  // products listed in CANVAS_EDITOR_EXCEPTION_SLUGS (framed canvas).
  const canvasWithoutEditor = isCanvasProduct && !canvasProductAllowsEditor(productSlug);
  const boogMaterial = getBoogMaterial(finishingOptions.material);
  const sizeValue = isBoog ? '920_x_940_mm' : configuredSizeValue;
  const firstSheet = productionSheetProfile?.pages[0];
  const editorSizeValue = firstSheet
    ? `${firstSheet.widthMm}_x_${firstSheet.heightMm}_mm`
    : editorUnavailable ? undefined : sizeValue;
  const sheetIntro = {
    nl: 'Ontwerp op de originele werkbladen. Controleer ieder blad; hulplijnen worden niet meegedrukt. Een los geüploade PDF wordt niet automatisch omgezet naar bewerkbare onderdelen.',
    en: 'Design on the original artwork sheets. Check every sheet; guides are not printed. An uploaded PDF is not automatically converted into editable parts.',
    de: 'Gestalten Sie auf den Original-Druckbögen. Prüfen Sie jeden Bogen; Hilfslinien werden nicht gedruckt. Eine hochgeladene PDF wird nicht automatisch in bearbeitbare Teile umgewandelt.',
    fr: 'Composez sur les planches originales. Vérifiez chaque planche ; les repères ne sont pas imprimés. Un PDF importé n’est pas automatiquement converti en éléments modifiables.',
  }[locale === 'en' || locale === 'de' || locale === 'fr' ? locale : 'nl'];
  const formatMillimeterDimensions = (widthMm: number, heightMm: number) => {
    const toCentimeters = (value: number) => {
      const cm = value / 10;
      return Number.isInteger(cm) ? String(cm) : String(Number(cm.toFixed(1))).replace('.', ',');
    };
    return `${toCentimeters(widthMm)} x ${toCentimeters(heightMm)} cm`;
  };
  const fileUploaderCopy = {
    nl: {
      upload: 'Uploaden',
      uploadOwnFile: 'Eigen bestand uploaden',
      searchImage: 'Zoek afbeelding',
      designerAvailable: 'Maak je ontwerp online',
      designerAvailableText: 'Upload eerst je foto of ontwerp. Daarna kun je tekst toevoegen, uitsnede aanpassen en het ontwerp bewerken.',
      uploading: 'Bestand uploaden...',
      dropHere: 'Laat je bestand hier los',
      dropOrClick: 'Sleep je bestand hier of klik om te uploaden',
      selectFile: 'Selecteer bestand',
      uploadSuccess: 'Upload succesvol',
      synced: 'Gesynchroniseerd met provider',
      otherFile: 'Ander bestand',
      designer: 'Designer',
      designerText: 'Voeg tekst toe, pas de uitsnede aan en sla je ontwerp op voor bestelling.',
      editDesign: 'Verder met je ontwerp',
      canvasEdges: 'Canvas zijkanten',
      canvasEdgesText: 'Kies hoe de zijkanten van je canvas eruit moeten zien',
      wrapped: 'Doorlopende zijkanten',
      wrappedText: 'Afbeelding loopt door (standaard)',
      white: 'Witte zijkanten',
      whiteText: 'Klassieke witte randen',
      black: 'Zwarte zijkanten',
      blackText: 'Moderne zwarte randen',
      stretched: 'Uitgetrokken zijkanten',
      stretchedText: 'Afbeelding uitrekken',
      mirrored: 'Gespiegelde zijkanten',
      mirroredText: 'Afbeelding spiegelen',
      failed: 'Upload mislukt',
      aiFailed: 'AI-bewerking niet gelukt',
      requirements: 'Bestandseisen:',
      fileType: 'Bestandstype: PDF (voorkeur), AI, EPS, SVG, JPG of PNG',
      resolution: 'Minimaal 300 DPI resolutie',
      color: 'CMYK kleurprofiel',
      bleed: '3mm afloop aan alle kanten',
      cutlineTitle: 'Snijlijn voor contourstickers',
      cutlineText: 'Kies bij configuratie voor through cut of kiss cut en gebruik in je PDF een vectorlijn met steunkleurnaam CutContour. Onze preview toont de snijlijn rood; houd tekst binnen de groene veilige zone.',
      tooLarge: `Bestand is te groot (max ${MAX_UPLOAD_LABEL})`,
      preparingFile: 'Printbestand voorbereiden...',
      finishingUpload: 'Upload afronden...',
      uploadStalled: 'De upload gaat langzaam. Houd dit tabblad open, we blijven het proberen.',
      uploadTimedOut: 'De upload liep vast. Controleer je verbinding en probeer het opnieuw.',
      uploadSlotSuffix: 'uploaden',
      designSlotSuffix: 'maken in designer',
      multiPhotoIntro: 'Upload je eerste foto. Die wordt automatisch in vak 1 geplaatst; daarna vul je de overige vakken in de editor.',
      pageUploadIntro: 'Upload een bestand voor dit onderdeel of maak het in de editor met tekst, afbeeldingen en vormen.',
      templateIntro: 'Upload je eigen bestand of open de editor. Daar kun je een beschikbare template aanpassen en je eigen tekst en afbeeldingen toevoegen.',
      defaultDesignerIntro: 'Heb je al een foto of PDF? Upload je bestand. Wil je online ontwerpen? Open de editor en voeg tekst, afbeeldingen en vormen toe.',
      designerLoading: 'Designer laden...',
      startDesigner: 'Editor openen',
      fileTypes: `PDF, AI, EPS, SVG, JPG of PNG - max ${MAX_UPLOAD_LABEL}`,
      aiFileCheck: 'AI bestandscheck',
      checkingFile: 'Bestand wordt gecontroleerd...',
      noIssues: 'Geen directe problemen gevonden. Controleer de preview voor je bestelt.',
    },
    de: {
      upload: 'Hochladen',
      uploadOwnFile: 'Eigene Datei hochladen',
      searchImage: 'Bild suchen',
      designerAvailable: 'Gestalten Sie Ihr Design online',
      designerAvailableText: 'Laden Sie zuerst Ihr Foto oder Design hoch. Danach können Sie Text hinzufügen, den Ausschnitt anpassen und das Design bearbeiten.',
      uploading: 'Datei wird hochgeladen...',
      dropHere: 'Datei hier ablegen',
      dropOrClick: 'Datei hierher ziehen oder zum Hochladen klicken',
      selectFile: 'Datei auswählen',
      uploadSuccess: 'Upload erfolgreich',
      synced: 'Mit Anbieter synchronisiert',
      otherFile: 'Andere Datei',
      designer: 'Designer',
      designerText: 'Fügen Sie Text hinzu, passen Sie den Ausschnitt an und speichern Sie Ihr Design für die Bestellung.',
      editDesign: 'Design weiterbearbeiten',
      canvasEdges: 'Leinwandseiten',
      canvasEdgesText: 'Wählen Sie, wie die Seiten Ihrer Leinwand aussehen sollen',
      wrapped: 'Umlaufende Seiten',
      wrappedText: 'Bild läuft um die Seiten herum (Standard)',
      white: 'Weiße Seiten',
      whiteText: 'Klassische weiße Ränder',
      black: 'Schwarze Seiten',
      blackText: 'Moderne schwarze Ränder',
      stretched: 'Gestreckte Seiten',
      stretchedText: 'Bild an den Seiten strecken',
      mirrored: 'Gespiegelte Seiten',
      mirroredText: 'Bild an den Seiten spiegeln',
      failed: 'Upload fehlgeschlagen',
      aiFailed: 'KI-Bearbeitung nicht gelungen',
      requirements: 'Dateianforderungen:',
      fileType: 'Dateityp: PDF (bevorzugt), AI, EPS, SVG, JPG oder PNG',
      resolution: 'Mindestens 300 DPI Auflösung',
      color: 'CMYK-Farbprofil',
      bleed: '3 mm Beschnitt an allen Seiten',
      cutlineTitle: 'Schnittlinie für Konturaufkleber',
      cutlineText: 'Wählen Sie im Konfigurator Through Cut oder Kiss Cut und verwenden Sie in der PDF eine Vektorlinie mit Sonderfarbname CutContour. Die Vorschau zeigt die Schnittlinie rot; halten Sie Text innerhalb der grünen Sicherheitszone.',
      tooLarge: `Datei ist zu groß (max. ${MAX_UPLOAD_LABEL})`,
      preparingFile: 'Druckdatei wird vorbereitet...',
      finishingUpload: 'Upload wird abgeschlossen...',
      uploadStalled: 'Der Upload ist langsam. Lassen Sie diesen Tab geöffnet, wir versuchen es weiter.',
      uploadTimedOut: 'Der Upload blieb hängen. Prüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
      uploadSlotSuffix: 'hochladen',
      designSlotSuffix: 'im Designer erstellen',
      multiPhotoIntro: 'Laden Sie zuerst Ihr erstes Foto hoch. Es wird automatisch in Feld 1 platziert; die übrigen Felder füllen Sie anschließend im Editor.',
      pageUploadIntro: 'Laden Sie eine Datei für diesen Teil hoch oder gestalten Sie ihn im Editor mit Text, Bildern und Formen.',
      templateIntro: 'Laden Sie Ihre eigene Datei hoch oder öffnen Sie den Editor. Dort können Sie eine verfügbare Vorlage anpassen und eigene Texte und Bilder hinzufügen.',
      defaultDesignerIntro: 'Sie haben schon ein Foto oder eine PDF? Laden Sie Ihre Datei hoch. Zum Gestalten öffnen Sie den Editor und fügen Text, Bilder und Formen hinzu.',
      designerLoading: 'Designer wird geladen...',
      startDesigner: 'Editor öffnen',
      fileTypes: `PDF, AI, EPS, SVG, JPG oder PNG - max. ${MAX_UPLOAD_LABEL}`,
      aiFileCheck: 'AI-Dateicheck',
      checkingFile: 'Datei wird geprüft...',
      noIssues: 'Keine direkten Probleme gefunden. Prüfen Sie die Vorschau vor der Bestellung.',
    },
    en: {
      upload: 'Upload',
      uploadOwnFile: 'Upload your own file',
      searchImage: 'Search image',
      designerAvailable: 'Create your design online',
      designerAvailableText: 'Upload your photo or design first. Then you can add text, adjust the crop and edit the design.',
      uploading: 'Uploading file...',
      dropHere: 'Drop your file here',
      dropOrClick: 'Drag your file here or click to upload',
      selectFile: 'Select file',
      uploadSuccess: 'Upload successful',
      synced: 'Synced with provider',
      otherFile: 'Other file',
      designer: 'Designer',
      designerText: 'Add text, adjust the crop and save your design for ordering.',
      editDesign: 'Continue your design',
      canvasEdges: 'Canvas sides',
      canvasEdgesText: 'Choose how the sides of your canvas should look',
      wrapped: 'Wrapped sides',
      wrappedText: 'Image continues around the sides (standard)',
      white: 'White sides',
      whiteText: 'Classic white edges',
      black: 'Black sides',
      blackText: 'Modern black edges',
      stretched: 'Stretched sides',
      stretchedText: 'Stretch image on the sides',
      mirrored: 'Mirrored sides',
      mirroredText: 'Mirror image on the sides',
      failed: 'Upload failed',
      aiFailed: 'AI edit unavailable',
      requirements: 'File requirements:',
      fileType: 'File type: PDF (preferred), AI, EPS, SVG, JPG or PNG',
      resolution: 'Minimum 300 DPI resolution',
      color: 'CMYK colour profile',
      bleed: '3mm bleed on all sides',
      cutlineTitle: 'Cut line for contour stickers',
      cutlineText: 'Choose through cut or kiss cut in the configurator and use a vector line named CutContour in your PDF. The preview shows the cut line in red; keep text inside the green safe zone.',
      tooLarge: `File is too large (max ${MAX_UPLOAD_LABEL})`,
      preparingFile: 'Preparing print file...',
      finishingUpload: 'Finishing upload...',
      uploadStalled: 'The upload is slow. Keep this tab open, we keep trying.',
      uploadTimedOut: 'The upload got stuck. Check your connection and try again.',
      uploadSlotSuffix: 'upload',
      designSlotSuffix: 'create in designer',
      multiPhotoIntro: 'Upload your first photo. It is placed in frame 1 automatically; then fill the remaining frames in the editor.',
      pageUploadIntro: 'Upload a file for this part or create it in the editor with text, images and shapes.',
      templateIntro: 'Upload your own file or open the editor. There you can customize an available template and add your own text and images.',
      defaultDesignerIntro: 'Already have a photo or PDF? Upload your file. To design online, open the editor and add text, images and shapes.',
      designerLoading: 'Loading designer...',
      startDesigner: 'Open editor',
      fileTypes: `PDF, AI, EPS, SVG, JPG or PNG - max ${MAX_UPLOAD_LABEL}`,
      aiFileCheck: 'AI file check',
      checkingFile: 'Checking file...',
      noIssues: 'No immediate issues found. Check the preview before ordering.',
    },
    fr: {
      upload: 'Télécharger',
      uploadOwnFile: 'Importer votre fichier',
      searchImage: 'Rechercher une image',
      designerAvailable: 'Créez votre design en ligne',
      designerAvailableText: 'Téléchargez d’abord votre photo ou design. Vous pourrez ensuite ajouter du texte, ajuster le cadrage et modifier le design.',
      uploading: 'Téléchargement du fichier...',
      dropHere: 'Déposez votre fichier ici',
      dropOrClick: 'Glissez votre fichier ici ou cliquez pour le télécharger',
      selectFile: 'Sélectionner un fichier',
      uploadSuccess: 'Téléchargement réussi',
      synced: 'Synchronisé avec le fournisseur',
      otherFile: 'Autre fichier',
      designer: 'Designer',
      designerText: 'Ajoutez du texte, ajustez le cadrage et enregistrez votre design pour la commande.',
      editDesign: 'Continuer votre création',
      canvasEdges: 'Côtés de la toile',
      canvasEdgesText: 'Choisissez l’apparence des côtés de votre toile',
      wrapped: 'Côtés enveloppés',
      wrappedText: 'L’image continue sur les côtés (standard)',
      white: 'Côtés blancs',
      whiteText: 'Bords blancs classiques',
      black: 'Côtés noirs',
      blackText: 'Bords noirs modernes',
      stretched: 'Côtés étirés',
      stretchedText: 'Étirer l’image sur les côtés',
      mirrored: 'Côtés miroir',
      mirroredText: 'Miroir de l’image sur les côtés',
      failed: 'Échec du téléchargement',
      aiFailed: 'Édition IA indisponible',
      requirements: 'Exigences du fichier :',
      fileType: 'Type de fichier : PDF (préféré), AI, EPS, SVG, JPG ou PNG',
      resolution: 'Résolution minimale de 300 DPI',
      color: 'Profil colorimétrique CMYK',
      bleed: 'Fond perdu de 3 mm sur tous les côtés',
      cutlineTitle: 'Ligne de coupe pour stickers contour',
      cutlineText: 'Choisissez through cut ou kiss cut dans le configurateur et utilisez une ligne vectorielle nommée CutContour dans votre PDF. La prévisualisation affiche la coupe en rouge ; gardez le texte dans la zone verte.',
      tooLarge: `Le fichier est trop volumineux (max. ${MAX_UPLOAD_LABEL})`,
      preparingFile: 'Préparation du fichier d’impression...',
      finishingUpload: 'Finalisation du téléchargement...',
      uploadStalled: 'Le téléchargement est lent. Gardez cet onglet ouvert, nous continuons d’essayer.',
      uploadTimedOut: 'Le téléchargement s’est bloqué. Vérifiez votre connexion et réessayez.',
      uploadSlotSuffix: 'à télécharger',
      designSlotSuffix: 'à créer dans le designer',
      multiPhotoIntro: 'Ajoutez votre première photo. Elle est placée automatiquement dans le cadre 1 ; remplissez ensuite les autres cadres dans l’éditeur.',
      pageUploadIntro: 'Importez un fichier pour cette partie ou créez-la dans l’éditeur avec du texte, des images et des formes.',
      templateIntro: 'Importez votre fichier ou ouvrez l’éditeur. Vous pourrez y personnaliser un modèle disponible et ajouter vos textes et images.',
      defaultDesignerIntro: 'Vous avez déjà une photo ou un PDF ? Importez votre fichier. Pour créer en ligne, ouvrez l’éditeur et ajoutez du texte, des images et des formes.',
      designerLoading: 'Chargement du designer...',
      startDesigner: 'Ouvrir l’éditeur',
      fileTypes: `PDF, AI, EPS, SVG, JPG ou PNG - max. ${MAX_UPLOAD_LABEL}`,
      aiFileCheck: 'Contrôle fichier IA',
      checkingFile: 'Contrôle du fichier en cours...',
      noIssues: 'Aucun problème direct trouvé. Vérifiez l’aperçu avant de commander.',
    },
  };
  const copy = fileUploaderCopy[locale.split('-')[0] as keyof typeof fileUploaderCopy] || fileUploaderCopy.nl;
  const HANDOFF_COPY = {
    nl: { start: 'Verder op je telefoon', scan: 'Scan met de camera van je telefoon', waiting: 'Wachten op je foto...', cancel: 'Toch niet' },
    de: { start: 'Weiter auf dem Telefon', scan: 'Mit der Handykamera scannen', waiting: 'Warten auf Ihr Foto...', cancel: 'Abbrechen' },
    en: { start: 'Continue on your phone', scan: 'Scan with your phone camera', waiting: 'Waiting for your photo...', cancel: 'Cancel' },
    fr: { start: 'Continuer sur le téléphone', scan: "Scannez avec l'appareil photo", waiting: 'En attente de votre photo...', cancel: 'Annuler' },
  } as const;
  const handoffCopy = HANDOFF_COPY[locale.split('-')[0] as keyof typeof HANDOFF_COPY] || HANDOFF_COPY.nl;
  // QR-overdracht naar de telefoon is op een telefoon zelf zinloos.
  const isHandheldDevice = useIsHandheldDevice();
  const UPLOAD_EXTRA_COPY = {
    nl: {
      collageBleed: 'Geen losse afloop nodig als je de collage-editor gebruikt; houd gezichten en tekst binnen de veilige marge',
      pendantBleed: 'Geen aparte afloop nodig; plaats belangrijke details binnen de veilige marge',
      colorProfile: 'RGB of CMYK kleurprofiel',
      designSaveFailed: 'Ontwerp kon niet worden opgeslagen.',
      designFileName: 'ontwerp',
      invalidFileType: 'Ongeldig bestandstype. Upload een PDF, AI, EPS, SVG, PNG of JPG bestand.',
      downloadFailed: 'Download mislukt',
      largeFileUnchecked: 'Dit grote bestand is niet automatisch gecontroleerd. Je originele drukbestand blijft behouden.',
      checkManually: 'Controleer formaat en drukkwaliteit handmatig.',
      fileCheckFailed: 'Bestandscheck mislukt',
      aiEditFailed: 'AI-bewerking mislukt',
      qualityExcellent: (dpi: number) => `Haarscherp (${dpi} DPI)`,
      qualityLow: (dpi: number) => `Lage resolutie (${dpi} DPI)`,
      qualityGood: (dpi: number) => `Goed (${dpi} DPI)`,
      removeBackground: 'Achtergrond weg',
      collageEditor: 'Collage editor',
      collageEditorText: 'Vul fotovakken met losse foto\'s en sla daarna het samengestelde printbestand op.',
      editCollage: 'Collage bewerken',
      chooseInsidePage: 'Binnenpagina kiezen',
      enhancePhotoAi: 'Foto verbeteren (AI)',
      savedPages: 'Opgeslagen:',
    },
    en: {
      collageBleed: 'No separate bleed needed when you use the collage editor; keep faces and text inside the safe margin',
      pendantBleed: 'No separate bleed needed; keep important details inside the safe margin',
      colorProfile: 'RGB or CMYK colour profile',
      designSaveFailed: 'Your design could not be saved.',
      designFileName: 'design',
      invalidFileType: 'Invalid file type. Please upload a PDF, AI, EPS, SVG, PNG or JPG file.',
      downloadFailed: 'Download failed',
      largeFileUnchecked: 'This large file was not checked automatically. Your original print file is kept as it is.',
      checkManually: 'Please check the size and print quality yourself.',
      fileCheckFailed: 'File check failed',
      aiEditFailed: 'AI edit failed',
      qualityExcellent: (dpi: number) => `Razor-sharp (${dpi} DPI)`,
      qualityLow: (dpi: number) => `Low resolution (${dpi} DPI)`,
      qualityGood: (dpi: number) => `Good (${dpi} DPI)`,
      removeBackground: 'Remove background',
      collageEditor: 'Collage editor',
      collageEditorText: 'Fill the photo frames with separate photos, then save the combined print file.',
      editCollage: 'Edit collage',
      chooseInsidePage: 'Choose inside page',
      enhancePhotoAi: 'Enhance photo (AI)',
      savedPages: 'Saved:',
    },
    de: {
      collageBleed: 'Kein separater Beschnitt nötig, wenn Sie den Collage-Editor verwenden; halten Sie Gesichter und Text innerhalb des Sicherheitsrands',
      pendantBleed: 'Kein separater Beschnitt nötig; platzieren Sie wichtige Details innerhalb des Sicherheitsrands',
      colorProfile: 'RGB- oder CMYK-Farbprofil',
      designSaveFailed: 'Ihr Design konnte nicht gespeichert werden.',
      designFileName: 'design',
      invalidFileType: 'Ungültiger Dateityp. Bitte laden Sie eine PDF-, AI-, EPS-, SVG-, PNG- oder JPG-Datei hoch.',
      downloadFailed: 'Download fehlgeschlagen',
      largeFileUnchecked: 'Diese große Datei wurde nicht automatisch geprüft. Ihre ursprüngliche Druckdatei bleibt unverändert erhalten.',
      checkManually: 'Bitte prüfen Sie Format und Druckqualität selbst.',
      fileCheckFailed: 'Dateiprüfung fehlgeschlagen',
      aiEditFailed: 'KI-Bearbeitung fehlgeschlagen',
      qualityExcellent: (dpi: number) => `Gestochen scharf (${dpi} DPI)`,
      qualityLow: (dpi: number) => `Niedrige Auflösung (${dpi} DPI)`,
      qualityGood: (dpi: number) => `Gut (${dpi} DPI)`,
      removeBackground: 'Hintergrund entfernen',
      collageEditor: 'Collage-Editor',
      collageEditorText: 'Füllen Sie die Fotofelder mit einzelnen Fotos und speichern Sie anschließend die zusammengesetzte Druckdatei.',
      editCollage: 'Collage bearbeiten',
      chooseInsidePage: 'Innenseite wählen',
      enhancePhotoAi: 'Foto verbessern (KI)',
      savedPages: 'Gespeichert:',
    },
    fr: {
      collageBleed: 'Pas de fond perdu séparé si vous utilisez l’éditeur de collage ; gardez les visages et le texte dans la marge de sécurité',
      pendantBleed: 'Pas de fond perdu séparé ; placez les détails importants dans la marge de sécurité',
      colorProfile: 'Profil colorimétrique RVB ou CMJN',
      designSaveFailed: 'Votre design n’a pas pu être enregistré.',
      designFileName: 'design',
      invalidFileType: 'Type de fichier non valide. Importez un fichier PDF, AI, EPS, SVG, PNG ou JPG.',
      downloadFailed: 'Échec du téléchargement',
      largeFileUnchecked: 'Ce fichier volumineux n’a pas été vérifié automatiquement. Votre fichier d’impression d’origine est conservé tel quel.',
      checkManually: 'Vérifiez vous-même le format et la qualité d’impression.',
      fileCheckFailed: 'Échec de la vérification du fichier',
      aiEditFailed: 'Échec de la retouche IA',
      qualityExcellent: (dpi: number) => `Très net (${dpi} DPI)`,
      qualityLow: (dpi: number) => `Basse résolution (${dpi} DPI)`,
      qualityGood: (dpi: number) => `Bon (${dpi} DPI)`,
      removeBackground: 'Supprimer l’arrière-plan',
      collageEditor: 'Éditeur de collage',
      collageEditorText: 'Remplissez les emplacements avec des photos séparées, puis enregistrez le fichier d’impression assemblé.',
      editCollage: 'Modifier le collage',
      chooseInsidePage: 'Choisir une page intérieure',
      enhancePhotoAi: 'Améliorer la photo (IA)',
      savedPages: 'Enregistrées :',
    },
  };
  const uploadExtraCopy = UPLOAD_EXTRA_COPY[locale.split('-')[0] as keyof typeof UPLOAD_EXTRA_COPY] || UPLOAD_EXTRA_COPY.nl;
  const vectorCopy = artworkCopy(locale);

  const isPageUpload = uploadMode === 'page-pdf';
  const isPhotoPendantProduct = String(productSlug || '').toLowerCase() === 'foto-hanger';
  const isMultiPhotoFrameProduct = String(productSlug || '').toLowerCase() === 'multi-fotolijst';
  const configuredBleedMm = parseMillimeters(finishingOptions.__bleedMm ?? finishingOptions.bleed);
  const formatBleedRequirement = (value: number) => {
    const formatted = Number.isInteger(value) ? String(value) : String(Number(value.toFixed(1))).replace('.', ',');
    if (locale === 'de') return `${formatted} mm Beschnitt an allen Seiten`;
    if (locale === 'en') return `${formatted} mm bleed on all sides`;
    if (locale === 'fr') return `Fond perdu de ${formatted} mm sur tous les côtés`;
    return `${formatted} mm afloop aan alle kanten`;
  };
  const bleedRequirementText = isBoog ? formatBleedRequirement(10) : isMultiPhotoFrameProduct
    ? uploadExtraCopy.collageBleed
    : isPhotoPendantProduct
    ? uploadExtraCopy.pendantBleed
    : configuredBleedMm !== null ? formatBleedRequirement(configuredBleedMm)
    : hasEditorSize(sizeValue) ? copy.bleed : productTemplateGuidance(locale);
  const colorRequirementText = isPhotoPendantProduct || isMultiPhotoFrameProduct ? uploadExtraCopy.colorProfile : copy.color;
  const configuredResolutionPpi = Number(isBoog ? 150 : finishingOptions.print_resolution);
  const resolutionRequirementText = Number.isFinite(configuredResolutionPpi) && configuredResolutionPpi > 0
    ? locale === 'de'
      ? `Empfohlen: ${configuredResolutionPpi} ppi im Endformat`
      : locale === 'en'
        ? `Recommended: ${configuredResolutionPpi} ppi at finished size`
        : locale === 'fr'
          ? `${configuredResolutionPpi} ppp recommandés au format fini`
          : `Aanbevolen: ${configuredResolutionPpi} ppi op eindformaat`
    : copy.resolution;
  const isContourStickerProduct = ['muursticker', 'vinylsticker'].includes(productSlug || '');
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(() => {
    if (initialFileUrl && initialFileName) {
      return {
        id: 'initial',
        fileName: initialFileName,
        fileSize: Number.isFinite(initialFileSize) && initialFileSize! > 0 ? initialFileSize! : 0,
        url: initialFileUrl,
        designData: initialDesignData,
      };
    }
    return null;
  });
  const [uploading, setUploading] = useState(false);
  // Alleen in de browser bepalen, anders verschilt de server-HTML van de client.
  const [inAppBrowser, setInAppBrowser] = useState<InAppBrowser | null>(null);
  useEffect(() => {
    setInAppBrowser(detectInAppBrowser(typeof navigator !== 'undefined' ? navigator.userAgent : ''));
  }, []);
  const [uploadProgress, setUploadProgress] = useState(0);
  // Voortgang met echte cijfers: een klant met een printbestand van honderden
  // MB's kijkt anders minutenlang naar een tolletje en herlaadt de pagina.
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('done');
  const [uploadLoaded, setUploadLoaded] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [uploadStalled, setUploadStalled] = useState(false);
  const lastProgressAtRef = useRef<number>(0);
  const latestProgressRef = useRef<UploadProgressDetail | null>(null);
  const stallReportedRef = useRef(false);
  const uploadStepRef = useRef<'file-upload' | 'design-upload'>('file-upload');
  const [error, setError] = useState<string | null>(null);
  // AI-fouten krijgen een eigen kop; de upload zelf is dan gewoon gelukt.
  const [errorTitle, setErrorTitle] = useState<string | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [savedDesign, setSavedDesign] = useState<any>(initialDesignData || null);
  useEffect(() => {
    if (isBoog && uploadedFile && !boogFileMatchesMaterial(uploadedFile, boogMaterial) && /\.pdf$/i.test(uploadedFile.fileName)) {
      setUploadedFile(null); setSavedDesign(null);
    }
  }, [isBoog, boogMaterial, uploadedFile]);
  const [activeEditorPage, setActiveEditorPage] = useState(1);
  const [pageDesigns, setPageDesigns] = useState<Record<number, any>>(() => {
    const existingPages = initialDesignData?.pageDesigns;
    return existingPages && typeof existingPages === 'object' ? existingPages : {};
  });
  const [printfulEditorTemplate, setPrintfulEditorTemplate] = useState<any>(null);
  const [printfulTemplateLoading, setPrintfulTemplateLoading] = useState(false);
  const [pendingEditorOpen, setPendingEditorOpen] = useState(false);
  const [printfulTemplateRequested, setPrintfulTemplateRequested] = useState(false);
  const [aiCheck, setAiCheck] = useState<any>(null);
  const fileCheckCacheRef = useRef<{ url: string; data: any } | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAction, setAiAction] = useState<string | null>(null);
  const [autoOpenedTemplateSlug, setAutoOpenedTemplateSlug] = useState<string | null>(null);

  // Grote editorstaten van een order staan buiten de JSON-kolom. Alleen laden
  // als deze uploader met zo'n order wordt geopend.
  useEffect(() => {
    if (!isStoredDesignDataReference(initialDesignData)) return;

    const controller = new AbortController();
    void fetch(initialDesignData.storageUrl, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Stored design ophalen mislukt (${response.status})`);
        return response.json();
      })
      .then((designData) => {
        if (controller.signal.aborted || !designData || typeof designData !== 'object') return;
        setSavedDesign(designData);
        if (designData.pageDesigns && typeof designData.pageDesigns === 'object') {
          setPageDesigns(designData.pageDesigns);
        }
        setUploadedFile((current) => current ? { ...current, designData } : current);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.warn('Stored editor design could not be loaded:', error);
      });

    return () => controller.abort();
  }, [initialDesignData?.storageUrl]);

  const readJsonResponse = async (response: Response) => {
    const text = await response.text();
    if (!text) return {};
    try {
      return JSON.parse(text);
    } catch {
      return {
        error: response.ok ? copy.failed : text.slice(0, 180) || copy.failed,
      };
    }
  };

  useEffect(() => {
    if (!initialDesignTemplateSlug || !sizeValue || autoOpenedTemplateSlug === initialDesignTemplateSlug) return;
    setAutoOpenedTemplateSlug(initialDesignTemplateSlug);
    if (providerType === 'PRINTFUL' && providerProductId && !printfulEditorTemplate) {
      setPendingEditorOpen(true);
      return;
    }
    setIsEditorOpen(true);
  }, [autoOpenedTemplateSlug, initialDesignTemplateSlug, sizeValue]);

  /**
   * Neemt de voortgang over die `uploadFileToStorage` doorgeeft. Diezelfde
   * gegevens gaan ook als `printen:upload-progress` het window op, zie
   * `@/lib/uploads/client-upload`.
   */
  const handleUploadProgress = useCallback((detail: UploadProgressDetail) => {
    lastProgressAtRef.current = Date.now();
    latestProgressRef.current = detail;
    setUploadStalled(false);
    setUploadPhase(detail.phase);
    setUploadLoaded(detail.loaded);
    setUploadTotal(detail.total);
    if (detail.phase !== 'error') setUploadProgress(detail.percentage);
  }, []);

  const beginUpload = useCallback((step: 'file-upload' | 'design-upload' = 'file-upload') => {
    uploadStepRef.current = step;
    lastProgressAtRef.current = Date.now();
    latestProgressRef.current = null;
    stallReportedRef.current = false;
    setUploadStalled(false);
    setUploadPhase('preparing');
    setUploadProgress(0);
    setUploadLoaded(0);
    setUploadTotal(0);
    setUploading(true);
  }, []);

  // Als er 15 seconden niets binnenkomt, zeggen we dat tegen de klant en
  // melden we het één keer. Stilte op een bestand van 300 MB laat mensen
  // herladen en hun werk kwijtraken.
  //
  // Dit is niet alleen kosmetisch: toen het CSP de rechtstreekse upload blokkeerde
  // bleef de SDK in stilte herproberen. De belofte werd nooit verworpen, dus
  // een melding die op een `catch` wacht komt in dat geval nooit. Een vastgelopen
  // upload moet zichtbaar zijn zonder dat er eerst iets moet falen.
  useEffect(() => {
    if (!uploading) {
      setUploadStalled(false);
      return;
    }
    const timer = setInterval(() => {
      if (!lastProgressAtRef.current || Date.now() - lastProgressAtRef.current <= 15000) return;
      setUploadStalled(true);
      if (stallReportedRef.current) return;
      stallReportedRef.current = true;
      const latest = latestProgressRef.current;
      reportOrderPathWarning(uploadStepRef.current, 'upload stalled', {
        product: productSlug || productId,
        providerType,
        bytes: latest?.total,
        loaded: latest?.loaded,
        percentage: latest?.percentage,
        phase: latest?.phase,
        stalledForMs: Date.now() - lastProgressAtRef.current,
      });
    }, 3000);
    return () => clearInterval(timer);
  }, [uploading, productSlug, productId, providerType]);

  /**
   * Slaat een data-URL uit de designer op.
   *
   * De designer levert het printbestand als base64 data-URL; dat is de enige
   * vorm die we krijgen (die code hoort bij een ander onderdeel). We zetten
   * hem daarom precies één keer om naar een File en laten de string daarna
   * los via `releaseSource`, zodat de base64-versie en de binaire versie niet
   * allebei blijven staan. Base64 is ~1,33x de bytes, dus dat scheelt op een
   * groot printbestand honderden MB's in het tabblad.
   */
  const uploadDataUrl = async (dataUrl: string, fileName: string, releaseSource?: () => void) => {
    if (!dataUrl || typeof dataUrl !== 'string') {
      throw new Error(uploadExtraCopy.designSaveFailed);
    }

    // Ook publiceren op window: het omzetten van een grote data-URL duurt
    // merkbaar lang, en meeluisteraars horen die fase net zo goed te zien als
    // de fasen die uploadFileToStorage zelf uitzendt.
    const preparing: UploadProgressDetail = { phase: 'preparing', loaded: 0, total: 0, percentage: 0 };
    handleUploadProgress(preparing);
    publishUploadProgress(preparing);

    let file: File;
    try {
      file = await dataUrlToFile(dataUrl, fileName);
      releaseSource?.();
    } catch (error) {
      reportOrderPathFailure('design-upload', error, {
        product: productSlug || productId,
        step: 'data-url-to-file',
        approximateBytes: typeof dataUrl === 'string' ? dataUrl.length : undefined,
      });
      throw new Error(copy.failed);
    }

    return uploadFileToStorage(file, {
      // Editorbestanden gaan altijd via de ondertekende browser-naar-Blob-route.
      // Ook kleine PDF's en tekstontwerpen omzeilen zo de serverless multipart-
      // grens en houden we de gebruiker niet afhankelijk van een serverupload.
      requireProof: true,
      productId,
      providerType,
      editorSessionId: getCurrentEditorSessionId({ productId, productSlug }) || undefined,
      failureMessage: copy.failed,
      timeoutMessage: copy.uploadTimedOut,
      step: 'design-upload',
      onProgress: handleUploadProgress,
    });
  };

  const uploadDesignData = async (designData: any): Promise<UploadedFile> => {
    const generatedPrintFile = designData?.printFile;
    const useGeneratedPdf =
      providerType !== 'PRINTFUL' &&
      typeof generatedPrintFile?.dataURL === 'string' &&
      generatedPrintFile.dataURL.startsWith('data:application/pdf');
    const dataUrl = useGeneratedPdf ? generatedPrintFile.dataURL : designData?.dataURL;
    const fileName = useGeneratedPdf
      ? generatedPrintFile.fileName
      : `${(productName || uploadExtraCopy.designFileName).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || uploadExtraCopy.designFileName}.png`;
    const upload = await uploadDataUrl(dataUrl, fileName, () => {
      // De data-URL van het gegenereerde printbestand wordt hierna toch niet
      // bewaard (zie `persistedDesignData`). Meteen loslaten voorkomt dat de
      // base64-string tijdens de upload naast de binaire kopie blijft staan.
      if (useGeneratedPdf && generatedPrintFile) generatedPrintFile.dataURL = undefined;
    });
    const sourceFile = designData?.sourceFile || (
      uploadedFile?.url && !/\.pdf(?:$|\?)/i.test(uploadedFile.url)
        ? {
            id: uploadedFile.id,
            url: uploadedFile.url,
            fileName: uploadedFile.fileName,
            fileSize: uploadedFile.fileSize,
          }
        : undefined
    );
    const compactedDesignData = compactDesignDataForPersistence(designData);
    // De bewerkbare staat ook los in Blob bewaren. De winkelwagen onthoudt in
    // de browser alleen deze verwijzing; zonder dat kwam het ontwerp na herladen
    // terug als één platte afbeelding. Mislukt dit, dan gaat opslaan gewoon door.
    let editorStateUrl: string | undefined;
    if (compactedDesignData && typeof compactedDesignData === 'object' && (compactedDesignData.json || compactedDesignData.pageDesigns)) {
      try {
        const { printFile: _printFile, editorStateUrl: _previous, storageUrl: _storage, ...editableState } = compactedDesignData;
        const stateFile = new globalThis.File([JSON.stringify(editableState)], `ontwerp-${Date.now()}.json`, { type: 'application/json' });
        const storedState = await uploadFileToStorage(stateFile, {
          productId,
          providerType,
          editorSessionId: getCurrentEditorSessionId({ productId, productSlug }) || undefined,
          failureMessage: copy.failed,
          timeoutMessage: copy.uploadTimedOut,
          step: 'design-upload',
        });
        editorStateUrl = storedState.url;
      } catch (error) {
        console.warn('Bewerkbare ontwerpstaat niet apart bewaard:', error);
      }
    }
    const persistedDesignData = useGeneratedPdf
      ? {
          ...compactedDesignData,
          ...(sourceFile ? { sourceFile } : {}),
          printFile: {
            ...compactedDesignData?.printFile,
            dataURL: undefined,
            uploadId: upload.id,
            url: upload.url,
            fileName: upload.fileName,
            fileSize: upload.fileSize,
            providerFileId: upload.providerFileId,
          },
        }
      : compactedDesignData;
    if (editorStateUrl && persistedDesignData && typeof persistedDesignData === 'object') {
      persistedDesignData.editorStateUrl = editorStateUrl;
    }
    // Het ontwerp is gemaakt op het voorbeeld van een nog niet omgezet
    // vectorbestand: die vlag moet mee, anders gaat het voorbeeld naar de pers.
    if (uploadedFile?.artworkConversion?.required && persistedDesignData && typeof persistedDesignData === 'object') {
      const previous = Array.isArray(persistedDesignData.artworkConversion?.files) ? persistedDesignData.artworkConversion.files : [];
      persistedDesignData.artworkConversion = { required: true, files: [
        ...previous.filter((file: any) => file?.url !== uploadedFile.url),
        { uploadId: uploadedFile.id, url: uploadedFile.url, fileName: uploadedFile.fileName },
      ] };
    }

    return {
      id: upload.id,
      fileName: upload.fileName,
      fileSize: upload.fileSize,
      url: upload.url,
      providerFileId: upload.providerFileId,
      designData: persistedDesignData,
    };
  };

  /**
   * Opslaan als wederverkoopproduct: drukbestand en bewerkbare staat via
   * precies dezelfde upload als een bestelling (uploadDesignData), plus de
   * preview als productafbeelding. Niet voor Printful, boekpagina's of Boog
   * (meer dan één drukbestand per product).
   */
  const absoluteUploadUrl = (url: string) => (url.startsWith('/') && typeof window !== 'undefined' ? `${window.location.origin}${url}` : url);
  const resellerSave = getResellerOptions && providerType !== 'PRINTFUL' && editorSurface !== 'inside-pages' && uploadMode !== 'page-pdf' && !isBoog
    ? {
        getOptions: getResellerOptions,
        persist: async (designData: any) => {
          beginUpload('design-upload');
          try {
            const previewSource = designData?.previewDataURL || designData?.dataURL;
            const slug = (productSlug || uploadExtraCopy.designFileName).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || uploadExtraCopy.designFileName;
            const preview = await uploadDataUrl(previewSource, `${slug}-preview.png`);
            const stored = await uploadDesignData(designData);
            return {
              fileUrl: absoluteUploadUrl(stored.url),
              previewUrl: absoluteUploadUrl(preview.url),
              designData: stored.designData || {},
              sizeValue: editorSizeValue || null,
            };
          } finally {
            setUploading(false);
          }
        },
      }
    : undefined;

  const normalizedPageCount = Math.max(1, Math.min(500, Number(pageCount || 1) || 1));
  const insidePageNumbers = editorSurface === 'inside-pages'
    ? Array.from({ length: normalizedPageCount }, (_, index) => index + 1)
    : [];
  const activePageDesign = editorSurface === 'inside-pages'
    ? pageDesigns[activeEditorPage] || null
    : savedDesign;
  const savedInsidePages = Object.keys(pageDesigns)
    .map((key) => Number(key))
    .filter((page) => Number.isFinite(page) && page > 0)
    .sort((a, b) => a - b);
  const currentEditorInitialData = editorSurface === 'inside-pages' ? activePageDesign : savedDesign;

  const createPrintfulMockupPreview = async (designData: any) => {
    const files = Array.isArray(designData?.printful?.files) ? designData.printful.files : [];
    const variantId = designData?.printful?.variantId;
    const printfulProductId = designData?.printful?.productId || providerProductId;
    if (providerType !== 'PRINTFUL' || !printfulProductId || !variantId || !files.length) return designData;

    const uploadedFiles = await Promise.all(files.map(async (file: any, index: number) => {
      try {
        if (!file?.dataURL || !file?.placement) return null;
        const placement = String(file.placement).toLowerCase().replace(/[^a-z0-9_-]+/g, '-') || `placement-${index + 1}`;
        const upload = await uploadDataUrl(file.dataURL, `printful-${variantId}-${placement}.png`, () => {
          // Wordt hierna vervangen door `printfulFiles` zonder data-URL.
          file.dataURL = undefined;
        });
        return {
          placement: file.placement,
          printfileId: file.printfileId ?? null,
          imageUrl: upload.url,
          width: file.width,
          height: file.height,
          printArea: file.printArea,
        };
      } catch (error) {
        console.warn('Printful mockup file upload skipped:', error);
        return null;
      }
    }));

    const printfulFiles = uploadedFiles.filter(Boolean);
    if (!printfulFiles.length) return designData;

    try {
      const response = await fetch('/api/printful/mockup-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: printfulProductId,
          variantId,
          files: printfulFiles.map((file: any) => ({
            placement: file.placement,
            imageUrl: file.imageUrl,
            printfileId: file.printfileId,
            width: file.width,
            height: file.height,
            printArea: file.printArea,
          })),
        }),
      });
      const data = await readJsonResponse(response);
      if (!response.ok || !data?.previewUrl) {
        return {
          ...designData,
          printful: { ...designData.printful, files: printfulFiles },
        };
      }

      return {
        ...designData,
        previewDataURL: data.previewUrl,
        mockupPreviewUrl: data.previewUrl,
        printful: {
          ...designData.printful,
          productId: printfulProductId,
          files: printfulFiles,
          mockups: data.mockups || [],
          mockupPreviewUrls: data.previewUrls || (data.previewUrl ? [data.previewUrl] : []),
          mockupTaskKey: data.taskKey,
        },
      };
    } catch {
      return {
        ...designData,
        printful: { ...designData.printful, files: printfulFiles },
      };
    }
  };

  const updateProductPreviewImage = (designData: any) => {
    const imageUrls = Array.isArray(designData?.printful?.mockupPreviewUrls)
      ? designData.printful.mockupPreviewUrls.filter((url: unknown): url is string => typeof url === 'string' && url.length > 0)
      : [];
    const imageUrl = designData?.mockupPreviewUrl || imageUrls[0] || null;
    if ((!imageUrl && imageUrls.length === 0) || typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('printen:product-preview-image', {
      detail: {
        productId,
        imageUrl,
        imageUrls: imageUrls.length ? imageUrls : imageUrl ? [imageUrl] : [],
        replaceImages: imageUrls.length > 0,
        variantId: designData?.printful?.variantId || null,
        placement: designData?.printful?.placement || null,
        source: 'saved-design',
      },
    }));
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];

    // Clear all previous state immediately
    setUploadedFile(null);
    setSavedDesign(null);
    setAiCheck(null);
    setError(null);
    setErrorTitle(null);
    beginUpload();

    // Client-side validatie. Deze twee gevallen zijn bewuste afwijzing en geen
    // defect, dus ze gaan niet naar Sentry.
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(copy.tooLarge);
      setUploading(false);
      return;
    }

    if (!isAcceptedArtworkFile(file)) {
      setError(uploadExtraCopy.invalidFileType);
      setUploading(false);
      return;
    }

    try {
      // De sessie EERST aanmaken/opzoeken en awaiten, niet er pas ná de
      // upload een keer op langsgaan: anders staat editorSessionId nooit op
      // de Upload-regel zelf, alleen de latere event-log kent de sessie nog.
      // Dat gaf een sessie met echte events maar toch "0 uploads" in
      // /admin/editor-sessions/[code], want die pagina leest de
      // Upload-relatie, geen event-log.
      const sessionCode = await getOrCreateEditorSession({ productId, productSlug, locale: locale || 'nl' });
      const editorSessionId = sessionCode ? getCurrentEditorSessionId({ productId, productSlug }) : undefined;

      // EPS, PS en oude AI: origineel bewaren en om een PDF vragen. Lukt dat
      // niet, dan gaat de klant gewoon door en zetten wij het om; nooit een
      // blokkade. PDF-compatibele AI en SVG volgen de gewone route.
      const { file: preparedFile, detection } = await prepareArtworkFile(file, vectorCopy.invalid);
      let upload: RegisteredUpload;
      let artworkConversion: UploadedFile['artworkConversion'];
      let sourceFile: { id: string; url: string; fileName: string; fileSize: number } | undefined;
      if (detection?.mimeType === POSTSCRIPT_MIME) {
        const result = await uploadAndConvertPostScript(preparedFile, {
          copy: { failed: copy.failed, invalid: vectorCopy.invalid },
          productId,
          providerType,
          editorSessionId: editorSessionId || undefined,
          onProgress: handleUploadProgress,
        });
        if (result.converted) {
          upload = result.converted;
          sourceFile = { id: result.original.id, url: result.original.url, fileName: result.original.fileName, fileSize: result.original.fileSize };
        } else {
          upload = result.original;
          artworkConversion = { required: true, previewUrl: result.previewUrl };
        }
      } else {
        // Onder de platformgrens gaat dit over /api/upload, daarboven
        // rechtstreeks naar Vercel Blob. De vorm van het antwoord is gelijk.
        upload = await uploadFileToStorage(preparedFile, {
          productId,
          providerType,
          editorSessionId: editorSessionId || undefined,
          failureMessage: copy.failed,
          invalidFileMessage: vectorCopy.invalid,
          timeoutMessage: copy.uploadTimedOut,
          step: 'file-upload',
          onProgress: handleUploadProgress,
        });
        // Een SVG kan de browser tonen, maar de drukker wil PDF.
        if (detection?.needsConversion) artworkConversion = { required: true, previewUrl: upload.url };
      }

      const uploadedFileData: UploadedFile = {
        id: upload.id,
        fileName: upload.fileName,
        fileSize: upload.fileSize,
        url: upload.url,
        providerFileId: upload.providerFileId,
        ...(artworkConversion ? { artworkConversion } : {}),
        ...(isBoog || sourceFile || artworkConversion ? { designData: {
          ...(isBoog ? { studioMaterial: boogMaterial } : {}),
          ...(sourceFile ? { sourceFile } : {}),
          ...(artworkConversion ? { artworkConversion: { required: true, files: [{ uploadId: upload.id, url: upload.url, fileName: upload.fileName }] } } : {}),
        } } : {}),
      };

      setUploadedFile(uploadedFileData);
      onFileUploaded(uploadedFileData);
      setUploadProgress(100);
      if (isMultiPhotoFrameProduct) {
        setIsEditorOpen(true);
      }

      if (sessionCode) {
        recordEditorSessionEvent('upload_added', {
          fileName: uploadedFileData?.fileName,
          fileSize: uploadedFileData?.fileSize,
        });
      }
    } catch (err) {
      // uploadFileToStorage heeft de echte storing al aan Sentry gemeld.
      setError(err instanceof Error ? err.message : copy.failed);
    } finally {
      setUploading(false);
    }
  }, [productId, productSlug, locale, providerType, onFileUploaded, copy.failed, copy.tooLarge, isMultiPhotoFrameProduct, isBoog, boogMaterial, beginUpload, handleUploadProgress, vectorCopy.invalid, uploadExtraCopy.invalidFileType]);

  /*
   * Verder op je telefoon.
   *
   * De originele foto staat op het toestel van de klant, niet op zijn laptop.
   * De laptop verzint een sessienummer, toont dat als QR, en kijkt daarna of er
   * iets binnenkomt. De telefoon uploadt langs dezelfde weg als hier, dus wat
   * binnenkomt is exact hetzelfde als wanneer de klant het bestand zelf had
   * gesleept - inclusief de databaseregel, want die maakt de telefoon al aan.
   */
  const [handoff, setHandoff] = useState<{ id: string; url: string } | null>(null);
  const seenHandoffFiles = useRef<Set<string>>(new Set());

  const startHandoff = useCallback(() => {
    const id = createSessionId();
    setHandoff({ id, url: `${window.location.origin}/${locale}/overzetten/${id}` });
  }, [locale]);

  useEffect(() => {
    if (!handoff) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    const tick = async () => {
      if (stopped) return;
      try {
        const response = await fetch(`/api/handoff/${handoff.id}`, { cache: 'no-store' });
        const data = await response.json();
        for (const file of Array.isArray(data?.files) ? data.files : []) {
          if (!file?.id || seenHandoffFiles.current.has(file.id)) continue;
          seenHandoffFiles.current.add(file.id);
          const incoming: UploadedFile = {
            id: String(file.id),
            fileName: String(file.fileName || 'foto'),
            fileSize: Number(file.fileSize) || 0,
            url: String(file.url),
            ...(isBoog ? { designData: { studioMaterial: boogMaterial } } : {}),
          };
          setUploadedFile(incoming);
          onFileUploaded(incoming);
          setHandoff(null);

          void getOrCreateEditorSession({ productId, productSlug, locale: locale || 'nl' }).then((code) => {
            if (!code) return;
            recordEditorSessionEvent('upload_added', {
              fileName: incoming.fileName,
              fileSize: incoming.fileSize,
            });
          });
          return;
        }
      } catch {
        // Een hapering mag het wachten niet afbreken; we kijken zo weer.
      }
      // Even lang volhouden als de afspraak zelf geldig is, niet langer.
      if (!stopped && Date.now() - startedAt < HANDOFF_TTL_MS) {
        timer = setTimeout(tick, 2500);
      }
    };

    timer = setTimeout(tick, 1500);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [handoff, onFileUploaded, isBoog, boogMaterial]);

  const { getRootProps, getInputProps, isDragActive, open: openFilePicker } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      // Browsers geven EPS/AI vaak geen of een wisselend type; de extensie
      // telt, de inhoud wordt daarna gecontroleerd.
      'application/postscript': ['.eps', '.ai', '.ps'],
      'image/svg+xml': ['.svg'],
    },
    maxFiles: 1,
    multiple: false,
  });

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Elke fase een eigen tekst. Een balk die op 100% blijft staan terwijl er nog
  // geregistreerd wordt, leest als vastgelopen.
  const uploadPhaseLabel =
    uploadPhase === 'preparing'
      ? copy.preparingFile
      : uploadPhase === 'finishing'
      ? copy.finishingUpload
      : copy.uploading;

  const renderUploadProgress = () => (
    <div className="space-y-3" data-testid="upload-progress">
      <div className="flex items-center gap-3">
        <Loader2 className="h-5 w-5 flex-shrink-0 animate-spin text-blue-600" />
        <div className="min-w-0 flex-1 text-left">
          <p className="truncate text-sm font-medium text-gray-900">{uploadPhaseLabel}</p>
          {uploadTotal > 0 && (
            <p className="mt-0.5 text-xs text-gray-500 tabular-nums" data-testid="upload-progress-bytes">
              {formatFileSize(Math.min(uploadLoaded, uploadTotal))} / {formatFileSize(uploadTotal)}
            </p>
          )}
        </div>
        <span className="text-sm font-semibold tabular-nums text-blue-700" data-testid="upload-progress-percentage">
          {uploadProgress}%
        </span>
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
        role="progressbar"
        aria-valuenow={uploadProgress}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={uploadPhaseLabel}
      >
        <div
          className="h-full rounded-full bg-blue-600 transition-all duration-200"
          style={{ width: `${uploadProgress}%` }}
        />
      </div>
      {uploadStalled && (
        <p className="text-xs text-amber-700" data-testid="upload-progress-stalled">{copy.uploadStalled}</p>
      )}
    </div>
  );

  const removeFile = () => {
    setUploadedFile(null);
    setSavedDesign(null);
    setAiCheck(null);
    setError(null);
    setUploadProgress(0);
    setUploading(false);
  };

  const handleImageFromUrl = async (imageUrl: string, fileName: string) => {
    // Clear all previous state immediately
    setUploadedFile(null);
    setSavedDesign(null);
    setAiCheck(null);
    setError(null);
    setErrorTitle(null);
    setUploading(true);
    setUploadProgress(0);

    // Reset canvas edge type to default (wrapped) for stock photos
    if (isCanvasProduct && onCanvasEdgeTypeChange) {
      onCanvasEdgeTypeChange('wrapped');
    }

    try {
      // Zelfde reden als bij onDrop hierboven: sessie eerst awaiten, anders
      // krijgt de Upload-regel nooit een editorSessionId.
      const sessionCode = await getOrCreateEditorSession({ productId, productSlug, locale: locale || 'nl' });
      const editorSessionId = sessionCode ? getCurrentEditorSessionId({ productId, productSlug }) : undefined;

      // Download de afbeelding via een proxy endpoint
      const response = await fetch('/api/images/download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageUrl,
          fileName,
          productId,
          providerType,
          editorSessionId: editorSessionId || undefined,
        }),
      });
      const data = await readJsonResponse(response);

      if (!response.ok || !data?.upload?.id) {
        // Een mislukte stockfoto blokkeert de bestelling net zo goed als een
        // mislukte upload; 400 is bewuste validatie en telt niet mee.
        if (response.status !== 400) {
          reportOrderPathFailure('file-upload', new Error(`Stockfoto downloaden mislukt (${response.status})`), {
            status: response.status,
            product: productSlug || productId,
            path: 'images-download',
            responseSnippet: typeof data?.error === 'string' ? data.error.slice(0, 180) : undefined,
          });
        }
        throw new Error(data.error || uploadExtraCopy.downloadFailed);
      }

      const uploadedFileData: UploadedFile = {
        id: data.upload.id,
        fileName: data.upload.fileName,
        fileSize: data.upload.fileSize,
        url: data.upload.url,
        providerFileId: data.upload.providerFileId,
        ...(isBoog ? { designData: { studioMaterial: boogMaterial } } : {}),
      };

      setUploadedFile(uploadedFileData);
      onFileUploaded(uploadedFileData);
      setUploadProgress(100);
      if (isMultiPhotoFrameProduct) {
        setIsEditorOpen(true);
      }

      if (sessionCode) {
        recordEditorSessionEvent('upload_added', {
          fileName: uploadedFileData?.fileName,
          fileSize: uploadedFileData?.fileSize,
        });
      }
    } catch (err) {
      console.error('Download error:', err);
      setError(err instanceof Error ? err.message : uploadExtraCopy.downloadFailed);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!uploadedFile?.url || !sizeValue) {
      setAiCheck(null);
      return;
    }
    // Een ontwerp uit de editor heeft zijn eigen meting (productionReadiness)
    // en een export op maat: niet nog eens downloaden en meten.
    // Elke uitslag krijgt het bestand en formaat mee waarvoor hij geldt, zodat
    // een vorige uitslag nooit even voor een nieuw bestand meetelt.
    const forFile = { forUrl: uploadedFile.url, forSize: sizeValue };
    if (isEditorDesign(uploadedFile.designData)) {
      setAiCheck({ status: 'ok', checked: true, fromEditor: true, ...forFile });
      return;
    }
    // Pixels van een foto hangen niet van het formaat af: bij een ander
    // formaat opnieuw rekenen, niet opnieuw downloaden.
    const cached = fileCheckCacheRef.current;
    if (cached && cached.url === uploadedFile.url && cached.data?.facts?.kind === 'image') {
      setAiCheck({ ...cached.data, ...forFile });
      return;
    }

    let cancelled = false;

    const controller = new AbortController();
    // Een EPS/PS/SVG kan de bestandscheck niet lezen; dat is geen fout van de
    // klant. Zeg wat er gebeurt: wij zetten het om en controleren het.
    if (uploadedFile.artworkConversion?.required) {
      setAiCheck({ status: 'warning', checked: false, warnings: [vectorCopy.needsConversion], suggestions: [], ...forFile });
      return () => { cancelled = true; controller.abort(); };
    }
    if (uploadedFile.fileSize > PDF_PREVIEW_MAX_BYTES) {
      setAiCheck({ status: 'warning', checked: false, warnings: [uploadExtraCopy.largeFileUnchecked], suggestions: [uploadExtraCopy.checkManually], ...forFile });
      return () => { cancelled = true; controller.abort(); };
    }
    const runCheck = async () => {
      try {
        const response = await fetch('/api/ai/file-check', {
          method: 'POST',
          signal: controller.signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileUrl: uploadedFile.url,
            fileName: uploadedFile.fileName,
            fileSize: uploadedFile.fileSize,
            sizeValue,
            productSlug,
            bleedMm: configuredBleedMm ?? undefined,
            bleedOutside: String(finishingOptions.__bleedOutside ?? '') === '1',
          }),
        });
        const data = await response.json();
        if (!cancelled) {
          if (response.ok) fileCheckCacheRef.current = { url: uploadedFile.url, data };
          setAiCheck(response.ok ? { ...data, ...forFile } : { status: 'error', warnings: [data?.error || uploadExtraCopy.fileCheckFailed], ...forFile });
        }
      } catch {
        if (!cancelled) {
          setAiCheck({ status: 'error', warnings: [uploadExtraCopy.fileCheckFailed], ...forFile });
        }
      }
    };

    setAiCheck(null);
    runCheck();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [uploadedFile?.url, uploadedFile?.fileName, uploadedFile?.fileSize, uploadedFile?.artworkConversion?.required, isEditorDesign(uploadedFile?.designData), sizeValue, productSlug, vectorCopy.needsConversion, configuredBleedMm, finishingOptions.__bleedOutside]);

  useEffect(() => {
    if (providerType !== 'PRINTFUL' || !providerProductId) {
      setPrintfulEditorTemplate(null);
      setPrintfulTemplateLoading(false);
      setPrintfulTemplateRequested(false);
      return;
    }

    const controller = new AbortController();
    const size = String(finishingOptions.size || finishingOptions.Maat || finishingOptions.Size || '');
    const color = String(finishingOptions.color || finishingOptions.Kleur || finishingOptions.Color || '');
    if (!size && !sizeValue) {
      setPrintfulEditorTemplate(null);
      setPrintfulTemplateLoading(false);
      setPrintfulTemplateRequested(false);
      return;
    }

    setPrintfulEditorTemplate(null);
    setPrintfulTemplateRequested(true);
    setPrintfulTemplateLoading(true);
    fetch(`/api/printful/editor-template?productId=${encodeURIComponent(providerProductId)}&size=${encodeURIComponent(size)}&sizeValue=${encodeURIComponent(sizeValue || '')}&color=${encodeURIComponent(color)}&v=3`, {
      signal: controller.signal,
      cache: 'no-store',
    })
      .then((response) => response.ok ? response.json() : null)
      .then((data) => {
        if (!data?.success) {
          setPrintfulEditorTemplate(null);
          return;
        }
        setPrintfulEditorTemplate(data);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') setPrintfulEditorTemplate(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setPrintfulTemplateLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [providerType, providerProductId, sizeValue, finishingOptions.size, finishingOptions.Maat, finishingOptions.Size, finishingOptions.color, finishingOptions.Kleur, finishingOptions.Color]);

  useEffect(() => {
    if (!pendingEditorOpen) return;
    if (providerType === 'PRINTFUL' && providerProductId && (printfulTemplateLoading || !printfulEditorTemplate)) return;
    setPendingEditorOpen(false);
    setIsEditorOpen(true);
  }, [pendingEditorOpen, providerType, providerProductId, printfulTemplateLoading, printfulEditorTemplate]);

  const openDesigner = () => {
    const printfulTemplateReady =
      providerType !== 'PRINTFUL' ||
      !providerProductId ||
      (printfulTemplateRequested && !printfulTemplateLoading && !!printfulEditorTemplate);

    if (!printfulTemplateReady) {
      setPendingEditorOpen(true);
      return;
    }
    setIsEditorOpen(true);
  };

  const printfulDesignerWaiting =
    providerType === 'PRINTFUL' &&
    !!providerProductId &&
    (!printfulTemplateRequested || printfulTemplateLoading || pendingEditorOpen || !printfulEditorTemplate);

  // Stoplicht van de bestandscheck. De server levert feiten, hier volgt het
  // oordeel voor het gekozen formaat (src/lib/file-check/print-file-check.ts).
  const fileCheckTarget = targetFromSizeValue(sizeValue);
  const isEditorDesignFile = Boolean(uploadedFile?.url) && isEditorDesign(uploadedFile?.designData);
  const fileCheckResult = useMemo(() => {
    if (!uploadedFile?.url) return null;
    if (isEditorDesign(uploadedFile.designData)) {
      return evaluatePrintFile({ facts: null, target: fileCheckTarget, fromEditor: true, editorReadiness: uploadedFile.designData?.productionReadiness });
    }
    // Zonder formaat draait er geen check; niet eindeloos "bezig" tonen.
    if (!sizeValue) return evaluatePrintFile({ facts: null, target: null });
    // Uitslag van een vorig bestand (of, bij een PDF, een vorig formaat): nog bezig.
    if (!aiCheck || aiCheck.forUrl !== uploadedFile.url) return null;
    if (aiCheck.facts?.kind !== 'image' && aiCheck.forSize !== sizeValue) return null;
    return evaluatePrintFile({ facts: aiCheck.facts || null, target: fileCheckTarget, productSlug, sizeOptions });
    // fileCheckTarget volgt sizeValue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiCheck, uploadedFile?.url, uploadedFile?.designData, sizeValue, productSlug, sizeOptions]);
  const fileCheckUncheckedNote = aiCheck && !aiCheck.facts && !aiCheck.fromEditor
    ? (aiCheck.status === 'error' ? uploadExtraCopy.fileCheckFailed : (aiCheck.warnings?.[0] as string | undefined) || null)
    : null;
  const fileCheckWantsUpscale = Boolean(fileCheckResult?.upscaleHelps)
    || Boolean(fileCheckResult?.issues.some((issue) => issue.code === 'low_dpi' && issue.severity === 'orange'));

  const fileCheckKey = fileCheckResult ? `${fileCheckResult.level}|${fileCheckResult.dpi ?? ''}|${fileCheckIssueCodes(fileCheckResult).join(',')}` : '';
  useEffect(() => {
    if (!uploadedFile?.url || !fileCheckResult) return;
    const codes = fileCheckIssueCodes(fileCheckResult);
    rememberFileCheck(uploadedFile.url, { level: fileCheckResult.level, dpi: fileCheckResult.dpi, codes });
    trackFileCheckResult({
      url: uploadedFile.url,
      sizeKey: sizeValue || '',
      level: fileCheckResult.level,
      codes,
      productSlug,
      source: isEditorDesign(uploadedFile.designData) ? 'editor' : 'upload',
    });
    // Alleen bij een andere uitkomst; het resultaatobject zelf is per render nieuw.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileCheckKey, uploadedFile?.url, sizeValue, productSlug]);

  const applyAiAction = async (action: 'upscale' | 'remove-background') => {
    if (!uploadedFile?.url) return;

    setAiLoading(true);
    setAiAction(action);

    try {
      const response = await fetch('/api/ai/image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          imageUrl: uploadedFile.url,
          // Precies de vergroting die de minimale dpi haalt (1 tot 8x).
          ...(action === 'upscale' && fileCheckResult?.dpi && fileCheckResult?.requiredDpi
            ? { factor: upscaleFactorFor(fileCheckResult.dpi, fileCheckResult.requiredDpi) }
            : {}),
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || uploadExtraCopy.aiEditFailed);
      }

      const updatedFile: UploadedFile = {
        ...uploadedFile,
        id: data.upload.id,
        fileName: data.upload.fileName,
        fileSize: data.upload.fileSize,
        url: data.upload.url,
        designData: undefined,
      };

      setSavedDesign(null);
      setUploadedFile(updatedFile);
      onFileUploaded(updatedFile);
    } catch (error) {
      setErrorTitle(copy.aiFailed);
      setError(error instanceof Error ? error.message : uploadExtraCopy.aiEditFailed);
    } finally {
      setAiLoading(false);
      setAiAction(null);
    }
  };

  return (
    <div className="space-y-4">
      {!uploadedFile ? (
        <>
        <Tabs defaultValue="upload" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload">
              <Upload className="h-4 w-4 mr-2" />
              {isPageUpload && slotLabel ? `${slotLabel} ${copy.uploadSlotSuffix}` : copy.upload}
            </TabsTrigger>
            <TabsTrigger value="search">
              <Search className="h-4 w-4 mr-2" />
              {copy.searchImage}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="mt-4">
            {inAppBrowser && (
              <p className="mb-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs leading-5 text-gray-700">
                {inAppBrowserNotice(locale, inAppBrowser)}
              </p>
            )}
            {editorUnavailable && !firstSheet && (
              <p className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs leading-5 text-gray-700">
                {{
                  nl: 'Voor deze uitvoering is de online ontwerptool niet beschikbaar. Maak het drukbestand zelf (bij een eigen vorm met een snijcontour, CutContour) en upload het hieronder.',
                  en: 'The online design tool is not available for this option. Prepare the print file yourself (for a custom shape, with a CutContour cut line) and upload it below.',
                  de: 'Für diese Ausführung ist das Online-Gestaltungstool nicht verfügbar. Erstellen Sie die Druckdatei selbst (bei eigener Form mit einer Schneidekontur, CutContour) und laden Sie sie unten hoch.',
                  fr: 'L’outil de création en ligne n’est pas disponible pour cette option. Préparez vous-même le fichier d’impression (pour une forme personnalisée, avec un contour de découpe CutContour) et téléversez-le ci-dessous.',
                }[locale === 'en' || locale === 'de' || locale === 'fr' ? locale : 'nl']}
              </p>
            )}
            {Number(finishingOptions.__contourCut) === 1 && !editorUnavailable && (
              <p data-contour-guidance="true" className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs leading-5 text-gray-700">
                {{
                  nl: 'Snijden op contour: maak je ontwerp in de online editor, dan zetten we de snijlijn automatisch om je ontwerp. Je kunt ook je eigen bestand uploaden; voeg daarin dan zelf een snijcontour (CutContour) toe.',
                  en: 'Contour cut: create your design in the online editor and we add the cut line around it automatically. You can also upload your own file; it then needs its own cut path (CutContour).',
                  de: 'Konturschnitt: Gestalten Sie Ihr Design im Online-Editor, dann setzen wir die Schnittlinie automatisch um Ihr Design. Sie können auch eine eigene Datei hochladen; diese braucht dann eine eigene Schneidekontur (CutContour).',
                  fr: 'Découpe à la forme : créez votre design dans l’éditeur en ligne et nous ajoutons automatiquement la ligne de découpe autour. Vous pouvez aussi téléverser votre propre fichier ; il doit alors contenir son propre contour de découpe (CutContour).',
                }[locale === 'en' || locale === 'de' || locale === 'fr' ? locale : 'nl']}
              </p>
            )}
            {/* Canvasproducten hebben geen designer-flow; daar geen designer beloven. */}
            {(providerType === 'PRINTFUL' ? Boolean(editorSizeValue?.trim()) : hasEditorSize(editorSizeValue)) && (!canvasWithoutEditor || providerType === 'PRINTFUL') && (
            <Card className="mb-4 border-rose-200 bg-rose-50 p-4">
              <div className="flex items-start gap-3">
                <Pencil className="mt-0.5 h-5 w-5 text-rose-600" />
                <div>
                  <h3 className="text-sm font-semibold text-rose-950">
                    {isPageUpload && slotLabel ? `${slotLabel} ${copy.designSlotSuffix}` : copy.designerAvailable}
                  </h3>
                  <p className="mt-1 text-xs leading-5 text-rose-800">
                    {productionSheetProfile ? sheetIntro : isBoog ? editorText(locale, 'Ontwerp de drie panelen met je eigen tekst, kleuren en afbeeldingen. Bekijk je tafel direct in 3D.') : isMultiPhotoFrameProduct
                      ? copy.multiPhotoIntro
                      : isPageUpload
                      ? copy.pageUploadIntro
                      : designTemplateCount > 0
                      ? copy.templateIntro
                      : copy.defaultDesignerIntro}
                  </p>
                  {editorSizeValue && !canvasWithoutEditor && (
                    <div className="mt-3 space-y-2">
                      {editorSurface === 'inside-pages' && (
                        <div>
                          <div className="mb-1 text-xs font-semibold text-rose-950">{uploadExtraCopy.chooseInsidePage}</div>
                          <div className="flex max-w-full flex-wrap gap-1 pb-1">
                            {insidePageNumbers.slice(0, 80).map((page) => (
                              <button
                                key={`inside-page-start-${page}`}
                                type="button"
                                onClick={() => setActiveEditorPage(page)}
                                className={`h-7 min-w-8 rounded border px-2 text-xs font-semibold ${activeEditorPage === page ? 'border-rose-600 bg-rose-600 text-white' : pageDesigns[page] ? 'border-rose-200 bg-white text-rose-700' : 'border-rose-200 bg-rose-50 text-rose-900'}`}
                              >
                                {page}
                              </button>
                            ))}
                            {insidePageNumbers.length > 80 && (
                              <span className="px-2 py-1 text-xs text-rose-800">+{insidePageNumbers.length - 80}</span>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                        <Button
                          type="button"
                          size="sm"
                          className="bg-rose-600 hover:bg-rose-700"
                          onClick={openDesigner}
                          disabled={printfulDesignerWaiting || uploading}
                        >
                          {printfulDesignerWaiting ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <Pencil className="mr-2 h-4 w-4" />
                          )}
                          {printfulDesignerWaiting ? copy.designerLoading : currentEditorInitialData ? copy.editDesign : copy.startDesigner}
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={openFilePicker} disabled={uploading}>
                          <Upload className="mr-2 h-4 w-4" />
                          {copy.uploadOwnFile}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Card>
            )}
            <div
              {...getRootProps()}
              className={`
                border-2 border-dashed rounded-lg p-12 text-center cursor-pointer
                transition-colors
                ${isDragActive
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                }
                ${uploading ? 'pointer-events-none opacity-60' : ''}
              `}
            >
              <input {...getInputProps()} />

              {uploading ? (
                <div className="mx-auto max-w-sm">{renderUploadProgress()}</div>
              ) : (
                <>
                  <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-base font-medium text-gray-900 mb-2">
                    {isDragActive
                      ? copy.dropHere
                      : copy.dropOrClick}
                  </p>
                  <p className="text-sm text-gray-500">
                    {copy.fileTypes}
                  </p>
                  <Button variant="outline" size="sm" className="mt-4">
                    {copy.selectFile}
                  </Button>
                </>
              )}
            </div>

            {/* De foto uit de fotorol is het origineel; wat een sociaal netwerk
                teruggeeft is een verkleinde kopie. Daarom deze omweg. Op een
                telefoon zelf is hij zinloos: de CSS-klasse verbergt hem al
                voor hydratatie op smalle aanraakschermen, de hook daarna ook
                in liggende stand. */}
            {!isHandheldDevice && (
            <div className="mt-3 [@media(pointer:coarse)_and_(max-width:767px)]:hidden">
              {handoff ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
                  <div className="mx-auto w-40 rounded-lg bg-white p-2 shadow-sm">
                    <QrCode text={handoff.url} className="h-auto w-full" title={handoffCopy.scan} />
                  </div>
                  <p className="mt-3 flex items-center justify-center gap-2 text-sm font-medium text-gray-900">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    {handoffCopy.waiting}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">{handoffCopy.scan}</p>
                  <button
                    type="button"
                    onClick={() => setHandoff(null)}
                    className="mt-3 text-xs font-medium text-gray-500 underline underline-offset-2 hover:text-gray-900"
                  >
                    {handoffCopy.cancel}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={startHandoff}
                  className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                >
                  <Smartphone className="h-4 w-4" />
                  {handoffCopy.start}
                </button>
              )}
            </div>
            )}
          </TabsContent>

          <TabsContent value="search" className="mt-4">
            <ImageSearch onImageSelected={handleImageFromUrl} locale={locale} />
          </TabsContent>
        </Tabs>
        </>
      ) : (
        <div className="space-y-4">
          {/* Voortgang van een opslag vanuit de designer: er ligt dan al een
              bestand, dus het uploadvak hierboven staat niet in beeld. */}
          {uploading && (
            <Card className="border-blue-200 bg-blue-50 p-4">
              {renderUploadProgress()}
            </Card>
          )}

          {/* Upload Success Card */}
          <Card className="p-4">
            <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 flex-1 items-start space-x-3">
                <div className="p-2 bg-green-50 rounded">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="mb-1 flex min-w-0 flex-wrap items-center gap-2">
                    <p className="min-w-0 max-w-full truncate break-all text-sm font-medium text-gray-900 sm:max-w-[28rem]">
                      {uploadedFile.fileName}
                    </p>
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      {formatFileSize(uploadedFile.fileSize)}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500">
                    {copy.uploadSuccess}
                    {uploadedFile.providerFileId && ` • ${copy.synced}`}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2 sm:ml-2 sm:flex-row sm:flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={removeFile}
                  disabled={uploading}
                  className="w-full sm:w-auto"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {copy.otherFile}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={removeFile}
                  disabled={uploading}
                  className="w-full sm:w-auto"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>

          {/* Een ontwerp uit de editor: de meldingen staan in de editor zelf
              (Meldingen). Hier alleen een rustige regel als het echt nodig is;
              bestellen kan altijd. */}
          {isEditorDesignFile && fileCheckResult && fileCheckResult.level !== 'green' && fileCheckResult.issues.length > 0 && (
            <p className="flex items-start gap-1.5 text-xs leading-5 text-amber-800" role="status" data-file-check="editor-notice">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>{fileCheckCopy(locale).issue[fileCheckResult.issues[0].code](fileCheckResult.issues[0], fileCheckTarget ? formatSizeCm(fileCheckTarget, locale) : '')}</span>
            </p>
          )}

          {!isMultiPhotoFrameProduct && !isEditorDesignFile && (
          <PrintFileCheckCard
            locale={locale}
            result={fileCheckResult}
            target={fileCheckTarget}
            uncheckedNote={fileCheckUncheckedNote}
            onSelectSmaller={onSelectSize ? (value) => {
              trackFileCheckFix('smaller_size', { level: fileCheckResult?.level || 'unchecked', productSlug });
              onSelectSize(value);
            } : undefined}
            onOpenEditor={editorSizeValue && uploadedFile.url ? () => {
              trackFileCheckFix('editor', { level: fileCheckResult?.level || 'unchecked', productSlug });
              openDesigner();
            } : undefined}
            dpiCheckerHref={fileCheckTarget && !/\.pdf$/i.test(uploadedFile.fileName)
              ? `/${locale}${dpiCheckerHref({ widthMm: fileCheckTarget.widthMm, heightMm: fileCheckTarget.heightMm, productSlug })}`
              : null}
            onDpiCheckerClick={() => trackFileCheckFix('dpi_checker', { level: fileCheckResult?.level || 'unchecked', productSlug })}
            actions={aiImageToolsEnabled ? (
                <div className="flex min-w-0 flex-none flex-col gap-2 sm:flex-row lg:flex-col">
                  <Button
                    type="button"
                    size="sm"
                    variant={fileCheckWantsUpscale ? 'default' : 'outline'}
                    disabled={aiLoading || /\.pdf$/i.test(uploadedFile.fileName) || Boolean(uploadedFile.artworkConversion)}
                    onClick={() => {
                      if (fileCheckResult && fileCheckResult.level !== 'green') trackFileCheckFix('upscale', { level: fileCheckResult.level, productSlug });
                      applyAiAction('upscale');
                    }}
                    className={`w-full whitespace-nowrap sm:w-auto lg:w-full font-semibold transition ${
                      fileCheckWantsUpscale
                        ? 'bg-rose-600 hover:bg-rose-700 text-white shadow-sm'
                        : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50'
                    }`}
                  >
                    {aiLoading && aiAction === 'upscale' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
                    {uploadExtraCopy.enhancePhotoAi}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={aiLoading || /\.pdf$/i.test(uploadedFile.fileName) || Boolean(uploadedFile.artworkConversion)}
                    onClick={() => applyAiAction('remove-background')}
                    className="w-full whitespace-nowrap border-slate-300 bg-white text-slate-800 hover:bg-slate-50 sm:w-auto lg:w-full font-medium"
                  >
                    {aiLoading && aiAction === 'remove-background' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Scissors className="mr-2 h-4 w-4" />}
                    {uploadExtraCopy.removeBackground}
                  </Button>
                </div>
            ) : null}
          />
          )}

          {/* Design Editor Button */}
          {editorSizeValue && uploadedFile.url && (!canvasWithoutEditor || providerType === 'PRINTFUL') && (
            <Card className="border-rose-200 bg-rose-50 p-4">
              <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-rose-950">{isMultiPhotoFrameProduct ? uploadExtraCopy.collageEditor : copy.designer}</h3>
                  <p className="mt-1 text-xs leading-5 text-rose-800">
                    {productionSheetProfile ? sheetIntro : isMultiPhotoFrameProduct ? uploadExtraCopy.collageEditorText : copy.designerText}
                  </p>
                </div>
                <Button size="sm" className="w-full whitespace-normal bg-rose-600 hover:bg-rose-700 sm:w-auto" onClick={openDesigner} disabled={uploading}>
                  <Pencil className="h-4 w-4 mr-2" />
                  {isMultiPhotoFrameProduct ? uploadExtraCopy.editCollage : copy.editDesign}
                </Button>
              </div>
              {editorSurface === 'inside-pages' && (
                <div className="mt-3 border-t border-rose-200 pt-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-rose-950">{uploadExtraCopy.chooseInsidePage}</span>
                    {savedInsidePages.length > 0 && (
                      <span className="text-xs text-rose-800">{uploadExtraCopy.savedPages} {savedInsidePages.join(', ')}</span>
                    )}
                  </div>
                  <div className="flex max-w-full flex-wrap gap-1 pb-1">
                    {insidePageNumbers.slice(0, 80).map((page) => (
                      <button
                        key={`inside-page-edit-${page}`}
                        type="button"
                        onClick={() => setActiveEditorPage(page)}
                        className={`h-8 min-w-9 rounded border px-2 text-xs font-semibold transition ${activeEditorPage === page ? 'border-rose-600 bg-rose-600 text-white' : pageDesigns[page] ? 'border-rose-200 bg-white text-rose-700 hover:bg-rose-100' : 'border-rose-200 bg-rose-50 text-rose-900 hover:bg-white'}`}
                      >
                        {page}
                      </button>
                    ))}
                    {insidePageNumbers.length > 80 && (
                      <span className="px-2 py-1 text-xs text-rose-800">+{insidePageNumbers.length - 80}</span>
                    )}
                  </div>
                </div>
              )}
            </Card>
          )}

          {isContourStickerProduct && (
            <Card className="border-amber-200 bg-amber-50 p-4">
              <h3 className="text-sm font-semibold text-amber-950">{copy.cutlineTitle}</h3>
              <p className="mt-1 text-xs leading-5 text-amber-800">{copy.cutlineText}</p>
            </Card>
          )}

          {/* Preview with Bleed Visualization */}
          {isBoog && savedDesign?.mockupPreviewUrl && (
            <Card className="overflow-hidden p-4">
              <img src={savedDesign.mockupPreviewUrl} alt={editorText(locale, 'BOOG met jouw opgeslagen ontwerp')} className="mx-auto max-h-[420px] w-full object-contain" />
              <p className="mt-3 text-sm text-stone-600">{editorText(locale, 'Je drie panelen zijn samen opgeslagen. Via ‘Verder met je ontwerp’ kun je elk paneel opnieuw bewerken.')}</p>
            </Card>
          )}
          {!productionSheetProfile && providerType !== 'PRINTFUL' && sizeValue && uploadedFile?.url && !isMultiPhotoFrameProduct && !isBoog
            && (!uploadedFile.artworkConversion || uploadedFile.artworkConversion.previewUrl) && (
            <DesignPreview
              productionFile={{ url: uploadedFile.url, fileName: uploadedFile.fileName }}
              fileSize={uploadedFile.fileSize}
              key={`${uploadedFile.id}-${canvasEdgeType}`} // Force re-render when edge type changes
              imageUrl={(savedDesign?.previewDataURL && savedDesign.previewDataURL.length > 0) ? savedDesign.previewDataURL : (savedDesign?.dataURL && savedDesign.dataURL.length > 0) ? savedDesign.dataURL : uploadedFile.artworkConversion?.previewUrl || uploadedFile.url}
              sizeValue={sizeValue}
              category={category}
              productName={productName}
              productSlug={productSlug}
              canvasEdgeType={canvasEdgeType}
              defaultShowGuides={true} // Always show guides by default (user can toggle off)
              finishingOptions={finishingOptions}
              printcomTemplates={providerType === 'PRINTCOM'}
            />
          )}

          {/* Canvas Edge Type Selection - Below preview for canvas products */}
          {isCanvasProduct && uploadedFile && onCanvasEdgeTypeChange && (
            <Card className="p-4 bg-purple-50 border-purple-200">
              <h3 className="text-sm font-medium text-purple-900 mb-3">{copy.canvasEdges}</h3>
              <p className="text-xs text-purple-700 mb-3">
                {copy.canvasEdgesText}
              </p>
              <RadioGroup value={canvasEdgeType} onValueChange={onCanvasEdgeTypeChange}>
                <div className="grid grid-cols-1 gap-2">
                  <label className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    canvasEdgeType === 'wrapped' ? 'border-purple-500 bg-purple-100' : 'border-gray-200 hover:border-purple-300'
                  }`}>
                    <RadioGroupItem value="wrapped" id="edge-wrapped" className="mr-3" />
                    <div className="flex-1">
                      <span className="text-sm font-medium">{copy.wrapped}</span>
                      <p className="text-xs text-gray-600 mt-1">{copy.wrappedText}</p>
                    </div>
                  </label>

                  <label className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    canvasEdgeType === 'white' ? 'border-purple-500 bg-purple-100' : 'border-gray-200 hover:border-purple-300'
                  }`}>
                    <RadioGroupItem value="white" id="edge-white" className="mr-3" />
                    <div className="flex-1">
                      <span className="text-sm font-medium">{copy.white}</span>
                      <p className="text-xs text-gray-600 mt-1">{copy.whiteText}</p>
                    </div>
                  </label>

                  <label className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    canvasEdgeType === 'black' ? 'border-purple-500 bg-purple-100' : 'border-gray-200 hover:border-purple-300'
                  }`}>
                    <RadioGroupItem value="black" id="edge-black" className="mr-3" />
                    <div className="flex-1">
                      <span className="text-sm font-medium">{copy.black}</span>
                      <p className="text-xs text-gray-600 mt-1">{copy.blackText}</p>
                    </div>
                  </label>

                  <label className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    canvasEdgeType === 'stretched' ? 'border-purple-500 bg-purple-100' : 'border-gray-200 hover:border-purple-300'
                  }`}>
                    <RadioGroupItem value="stretched" id="edge-stretched" className="mr-3" />
                    <div className="flex-1">
                      <span className="text-sm font-medium">{copy.stretched}</span>
                      <p className="text-xs text-gray-600 mt-1">{copy.stretchedText}</p>
                    </div>
                  </label>

                  <label className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    canvasEdgeType === 'mirrored' ? 'border-purple-500 bg-purple-100' : 'border-gray-200 hover:border-purple-300'
                  }`}>
                    <RadioGroupItem value="mirrored" id="edge-mirrored" className="mr-3" />
                    <div className="flex-1">
                      <span className="text-sm font-medium">{copy.mirrored}</span>
                      <p className="text-xs text-gray-600 mt-1">{copy.mirroredText}</p>
                    </div>
                  </label>
                </div>
              </RadioGroup>
            </Card>
          )}
        </div>
      )}

      {error && (
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900 mb-1">{errorTitle || copy.failed}</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setError(null); setErrorTitle(null); }}
              className="ml-2"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}

      {/* File Requirements */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <p className="text-xs font-medium text-blue-900 mb-2">{copy.requirements}</p>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• {copy.fileType}</li>
          <li>• {resolutionRequirementText}</li>
          <li>• {colorRequirementText}</li>
          <li>• {bleedRequirementText}</li>
        </ul>
      </Card>

      {/* Design Editor Modal */}
      {editorSizeValue && isEditorOpen && (providerType !== 'PRINTFUL' || !providerProductId || printfulEditorTemplate) && (
        <DesignEditorModal
          draftScope={draftScope}
          imageUrl={productionSheetProfile || currentEditorInitialData?.json ? '' : uploadedFile?.artworkConversion
            ? uploadedFile.artworkConversion.previewUrl || artworkPlaceholderDataUrl(uploadedFile.fileName)
            : uploadedFile?.url || ''}
          sizeValue={editorSizeValue}
          productionSheetProfile={productionSheetProfile}
          category={category}
          productName={productName}
          productId={productId}
          productSlug={productSlug}
          providerType={providerType}
          printfulTemplate={printfulEditorTemplate}
          canvasEdgeType={canvasEdgeType}
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          initialCanvasData={editorSurface === 'inside-pages' ? { pageDesigns } : currentEditorInitialData}
          finishingOptions={finishingOptions}
          guideTemplateUrl={guideTemplateUrl}
          designPlacements={designPlacements}
          preferredDesignTemplateSlug={initialDesignTemplateSlug}
          locale={locale}
          editorSurface={editorSurface}
          pageCount={normalizedPageCount}
          initialPage={activeEditorPage}
          aiImageToolsEnabled={aiImageToolsEnabled}
          resellerSave={resellerSave}
          onSave={async (designData) => {
            // Opslaan vanuit de designer loopt via dezelfde uploadstroom als
            // een sleep-en-neerzet, dus ook hier voortgang tonen. De designer
            // heeft een eigen overlay; die kan zich op het window-event
            // `printen:upload-progress` abonneren (nog te doen, dat bestand is
            // op dit moment van een ander).
            beginUpload('design-upload');
            try {
              const enrichedDesignData = await createPrintfulMockupPreview(designData);
              const hasGeneratedPrintPdf =
                providerType !== 'PRINTFUL' &&
                typeof enrichedDesignData?.printFile?.dataURL === 'string';
              const needsLowResolutionPreviewUpload = enrichedDesignData?.productionReadiness?.reason === 'LOW_RESOLUTION';
              const updatedFile = hasGeneratedPrintPdf || !uploadedFile || needsLowResolutionPreviewUpload
                ? await uploadDesignData(enrichedDesignData)
                : { ...uploadedFile, designData: enrichedDesignData };
              const storedDesignData = updatedFile.designData || enrichedDesignData;

              if (editorSurface === 'inside-pages') {
                const nextPages = storedDesignData.pageDesigns || pageDesigns;
                setPageDesigns(nextPages);
                if (storedDesignData.activePage) setActiveEditorPage(Number(storedDesignData.activePage) || 1);
                setSavedDesign({ ...storedDesignData, pageDesigns: nextPages });
              } else {
                setSavedDesign(storedDesignData);
              }
              updateProductPreviewImage(storedDesignData);
              window.printenTrack?.('design_saved', {
                productName,
                productSlug,
                contentType: 'design',
              });

              setUploadedFile(updatedFile);
              onFileUploaded(updatedFile);
              setIsEditorOpen(false);
              onDesignSaved?.();
            } catch (error) {
              // De onderliggende storing is al gemeld door uploadFileToStorage
              // of uploadDataUrl. Deze melding vangt alles wat daarbuiten valt,
              // zodat een geblokkeerde opslag nooit meer alleen als toast bij
              // de klant eindigt.
              if (!(error instanceof UploadValidationError)) {
                reportOrderPathFailure('design-save', error, {
                  product: productSlug || productId,
                  providerType,
                  editorSurface,
                });
              }
              const message = error instanceof Error ? error.message : copy.failed;
              setError(message);
              throw error;
            } finally {
              setUploading(false);
            }
          }}
        />
      )}
    </div>
  );
}
